import Backbone from 'backbone'
import template from './collapsible.hbs'
import _ from 'lodash'
import $ from 'jquery'
import constants from 'scoreboard/components/constants'
const { BORDER_WIDTH } = constants

/**
 * A container with a header and collapsible content.
 *
 * Example:
 *   new Collapsible({
 *     header: new SomeHeaderView(),
 *     content: new SomeContentView(),
 *     collapsed: false,
 *     expandTarget: '.expand-button',
 *     collapseTarget: '.collapse-button',
 *     enableHeader: true,
 *     replace: false,
 *     maxHeight: 400 // optional
 *   })
 *
 * If you'd like to prevent the panel from toggling when clicking on a certain
 * element, you can add the 'no-toggle-trigger' class to that element.
 */
var Collapsible = Backbone.View.extend({
	className: 'collapsible panel',

	/**
	 *
	 * @param {String|Backbone.View} [options.header=''] A view or html string
	 *
	 * @param {String|Backbone.View} [options.content=''] A view or html string
	 *
	 * @param {boolean} [options.replace=false] The default behavior is to append the header
	 * and content to the template, pass true to replace the header and container panels
	 * with the provided views
	 *
	 * @param {Function|Number} [options.maxHeight]
	 *
	 * @param {Boolean} [options.collapsed=true]
	 *
	 * @param {Boolean} [options.collapsible=true]
	 */
	initialize: function (options = {}) {
		this.options = _.extend({
			collapsed: true,
			collapsible: true
		}, options)
		_.bindAll(this, 'resize')
	},

	render: function () {
		this.$el.toggleClass('is-collapsible', this.options.collapsible)
		this.$el.append(template())

		const {header = '', content = ''} = this.options
		// Get the content or Backbone.View, but don't render until it's attached
		const headerEl = header.render ? header.el : header
		const contentEl = content.render ? content.el : content

		if (this.options.replace) {
			this.$('> .panel-header').replaceWith(headerEl)
			this.$('> .content-container').replaceWith(contentEl)
		} else {
			this.$('> .panel-header').append(headerEl)
			this.$('> .content-container').append(contentEl)
		}
		_.invokeMap([header, content], 'render')

		this.prepare()
		return this
	},

	/**
	 * Setup click listeners and prepare the initial state (toggles, resize, etc)
	 */
	prepare: function() {
		this._addListeners()

		// If it starts collapsed, just collapse from the beginning
		if (this.options.collapsed) {
			this.toggle(!this.options.collapsed)
		} else {
			// If it starts expanded, give the view time to render before doing the expand,
			// so the height is correct
			process.nextTick(() => this.toggle(!this.options.collapsed))
		}

		this._resetCollapseTriggers()
	},

	_addListeners: function() {
		// If window is resized, recalculate the height because elements may have
		// wrapped/unwrapped. This will ensure they don't get cutt off/hidden
		$(window).on('resize', this.resize)

		// Setup the click handler for toggling
		const hasTargets = !_.isNil(this.options.expandTarget) && !_.isNil(this.options.collapseTarget)
		if (hasTargets) {
			this.expandTarget = this.$(this.options.expandTarget)
			this.collapseTarget = this.$(this.options.collapseTarget)
			this.expandTarget.on('click', this._onTargetClick.bind(this))
				.addClass('custom-trigger')
			this.collapseTarget.on('click', this._onTargetClick.bind(this))
				.addClass('custom-trigger')
			this.$el.addClass('has-custom-triggers')
		}

		if (!hasTargets || this.options.enableHeader) {
			this.$('.panel-header').on('click', e => {
				// If we've clicked on a button which we don't want to trigger
				// a collapse/expand, don't collapase/expand
				if (!_.isEmpty($(e.target).closest('.no-toggle-trigger'))) {
					return
				}
				this.toggle()
			})
		}

		this.$('.content-container').on('transitionend', () => this.trigger('toggleEnd'))
	},

	_onTargetClick: function(e) {
		// Stop event in case header and target's are both enabled
		e.stopPropagation()
		e.preventDefault()

		this.toggle()
	},

	collapsed: function () {
		return this.$el.hasClass('is-collapsed')
	},

	toggle: function (expand) {
		if (!this.options.collapsible) {
			return
		}

		expand = !_.isNil(expand) ? expand : this.collapsed()
		this.$el.toggleClass('is-collapsed', !expand)
		this.resize()
		this.trigger(expand ? 'expand' : 'collapse', this)
		this._resetCollapseTriggers()
	},

	// Sets the visibility of the custom expand/collapse targets (if they exist)
	// based on whether the panel is collapsed or not
	_resetCollapseTriggers: function() {
		if (!_.isNil(this.expandTarget) && !_.isNil(this.collapseTarget)) {
			this.expandTarget.toggleClass('is-not-displayed', !this.collapsed())
			this.collapseTarget.toggleClass('is-not-displayed', this.collapsed())
		}
	},

	resize: function (noTransition) {
		var doResize = () => {
			// this.trigger('resize')
			var container = this.$('> .content-container')
			// Ensure the max-height matches the actual height of the contents + 1px border
			// or whatever has been passed in as the default toggle height
			let outerHeight = container.children().outerHeight() + BORDER_WIDTH
			var maxHeight = _.result(this.options, 'maxHeight', outerHeight)
			container.css('max-height', this.collapsed() ? 0 : maxHeight)
		}

		if (noTransition) {
			this.toggleTransitions(false)
			doResize()
			// Wait till the next tick to ensure there is no transition done
			// during this resize.
			process.nextTick(()=> this.toggleTransitions(true))
		} else {
			doResize()
		}
	},

	toggleTransitions: function(transitions) {
		this.$el.toggleClass('no-transitions', !transitions)
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this)
		$(window).off('resize', this.resize)
		_.forEach([this.options.header, this.options.content], function (child) {
			child && child.remove && child.remove()
		})
	}
})

export default Collapsible

Collapsible.__filename__ = 'components/collapsible.js'
